// ⚠️  DOC-UPDATE: UI changes here may require updates to:
//     • client/public/class-guide.html   (skill tree UI, node interactions)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/SkillTree.css';
import { formatCurrency } from './shared/formatCompact';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// Choice options for requiresChoice AC abilities
const AC_CHOICE_OPTIONS = {
  monsterType: ['Humanoid', 'Beast', 'Undead', 'Elemental', 'Construct', 'Dragon', 'Demon', 'Aberration'],
  psionDiscipline: ['Psychokinesis', 'Telepathy', 'Clairsentience', 'Metacreativity', 'Psychoportation'],
  element: ['Fire', 'Water', 'Earth', 'Air', 'Cold', 'Electricity', 'Acid']
};

// ============================================================
// Skill Tree Panel — full-screen overlay with tabs for
// Base Class skills and Advanced Class perks
// ============================================================
const SkillTreePanel = ({ character, onClose, onCharacterUpdate }) => {
  // Which top-level view: 'base' or 'advanced'
  const [activeView, setActiveView] = useState('base');

  // ---- Base Skill Tree state ----
  const [treeData, setTreeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [activeBranch, setActiveBranch] = useState('all');
  const [showRespecConfirm, setShowRespecConfirm] = useState(false);
  const [isAllocating, setIsAllocating] = useState(false);
  const [recentlyAllocated, setRecentlyAllocated] = useState(null);
  // v640.4: Tick every second to update soft-undo countdown display
  const [undoTick, setUndoTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setUndoTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const contentRef = useRef(null);
  const nodeRefs = useRef({});
  const [lines, setLines] = useState([]);
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });

  // ---- Advanced Class state ----
  const [acTree, setAcTree] = useState(null);
  const [acLoading, setAcLoading] = useState(false);
  const [acError, setAcError] = useState(null);
  const [acChoices, setAcChoices] = useState({});
  const [activeAuras, setActiveAuras] = useState([]);
  const [learnedAbilities, setLearnedAbilities] = useState([]);
  const [acBusy, setAcBusy] = useState(null);

  const apiCall = useCallback(async (endpoint, method = 'GET', body = null) => {
    const token = localStorage.getItem('token');
    const opts = {
      method,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    };
    if (body) opts.body = JSON.stringify(body);
    const res = await fetch(`${API_BASE_URL}${endpoint}`, opts);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  }, []);

  // ============================================================
  // BASE SKILL TREE — fetch & actions
  // ============================================================
  const fetchTree = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/player/skill-tree');
      // v640.4: stamp absolute expiry so the countdown stays correct across renders
      if (data.lastAllocation && data.lastAllocation.remainingMs) {
        data.lastAllocation.expiresAt = Date.now() + data.lastAllocation.remainingMs;
      }
      setTreeData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { fetchTree(); }, [fetchTree]);

  // Calculate SVG connection lines between prerequisite nodes
  const updateLines = useCallback(() => {
    if (!treeData || !contentRef.current) return;
    const container = contentRef.current;
    const containerRect = container.getBoundingClientRect();
    const newLines = [];

    for (const [nodeId, node] of Object.entries(treeData.nodes)) {
      if (!node.requires || node.requires.length === 0) continue;
      if (activeBranch !== 'all' && activeBranch !== node.branch && node.branch !== 'trunk') continue;

      const childEl = nodeRefs.current[nodeId];
      if (!childEl) continue;
      const childRect = childEl.getBoundingClientRect();

      for (const reqId of node.requires) {
        const parentEl = nodeRefs.current[reqId];
        if (!parentEl) continue;
        const parentRect = parentEl.getBoundingClientRect();

        const x1 = parentRect.left + parentRect.width / 2 - containerRect.left + container.scrollLeft;
        const y1 = parentRect.top + parentRect.height - containerRect.top + container.scrollTop;
        const x2 = childRect.left + childRect.width / 2 - containerRect.left + container.scrollLeft;
        const y2 = childRect.top - containerRect.top + container.scrollTop;

        const parentState = getNodeState(reqId);
        const childState = getNodeState(nodeId);
        const isActive = parentState === 'allocated' && childState === 'allocated';
        const isReady = parentState === 'allocated' && childState === 'available';
        const branchInfo = treeData.branches.find(b => b.id === node.branch);
        const color = isActive ? (branchInfo?.color || '#4a6a4a') : isReady ? '#4a5a8a' : '#2a2a2a';

        newLines.push({ x1, y1, x2, y2, color, isActive, isReady, key: `${reqId}->${nodeId}` });
      }
    }
    setLines(newLines);
    setSvgSize({ width: container.scrollWidth, height: container.scrollHeight });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData, activeBranch]);

  useEffect(() => {
    const timer = setTimeout(updateLines, 100);
    return () => clearTimeout(timer);
  }, [updateLines, treeData, activeBranch]);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;
    const onScroll = () => updateLines();
    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [updateLines]);

  const handleAllocate = async (nodeId) => {
    if (isAllocating) return;
    try {
      setIsAllocating(true);
      const data = await apiCall('/api/player/allocate-skill', 'POST', { nodeId });
      setTreeData(prev => ({
        ...prev,
        allocated: data.allocated,
        available: data.available,
        totalAllocated: prev.totalEarned - data.available,
        effects: data.effects,
        chosenBranch: data.chosenBranch,
        lastAllocation: data.lastAllocation
          ? { ...data.lastAllocation, expiresAt: Date.now() + data.lastAllocation.remainingMs }
          : null,
      }));
      setRecentlyAllocated(nodeId);
      setTimeout(() => setRecentlyAllocated(null), 1200);
      setStatusMsg(`✅ Learned ${treeData.nodes[nodeId].name}!`);
      setSelectedNode(null);
      if (onCharacterUpdate) onCharacterUpdate();
      setTimeout(() => setStatusMsg(null), 3000);
    } catch (err) {
      setStatusMsg(`❌ ${err.message}`);
      setTimeout(() => setStatusMsg(null), 3000);
    } finally {
      setIsAllocating(false);
    }
  };

  // v640.4: Undo most recent skill allocation (60s soft-undo)
  const handleUndoSkill = async () => {
    try {
      const data = await apiCall('/api/player/undo-skill', 'POST');
      setTreeData(prev => ({
        ...prev,
        allocated: data.allocated,
        available: data.available,
        totalAllocated: prev.totalEarned - data.available,
        effects: data.effects,
        chosenBranch: data.chosenBranch,
        lastAllocation: null,
      }));
      setStatusMsg(`↩️ ${data.message}`);
      setSelectedNode(null);
      if (onCharacterUpdate) onCharacterUpdate();
      setTimeout(() => setStatusMsg(null), 3000);
    } catch (err) {
      setStatusMsg(`❌ ${err.message}`);
      setTimeout(() => setStatusMsg(null), 3000);
    }
  };

  const handleRespec = async () => {
    try {
      const data = await apiCall('/api/player/respec-skills', 'POST');
      setTreeData(prev => ({
        ...prev,
        allocated: data.allocated,
        available: data.available,
        totalAllocated: 0,
        gold: data.gold,
        effects: data.effects,
        chosenBranch: data.chosenBranch,
        lastAllocation: null,
      }));
      setStatusMsg(`✅ ${data.message}`);
      setShowRespecConfirm(false);
      setSelectedNode(null);
      if (onCharacterUpdate) onCharacterUpdate();
      setTimeout(() => setStatusMsg(null), 4000);
    } catch (err) {
      setStatusMsg(`❌ ${err.message}`);
      setTimeout(() => setStatusMsg(null), 3000);
    }
  };

  const canAllocate = (nodeId) => {
    if (!treeData || treeData.available <= 0) return false;
    if (treeData.allocated[nodeId]) return false;
    const node = treeData.nodes[nodeId];
    if (!node) return false;
    if (treeData.available < node.cost) return false;
    if (node.levelRequired && treeData.level < node.levelRequired) return false;
    // race_affinity (Heritage) is exempt from branch-lock — it always remains available regardless of chosen specialization.
    if (node.branch !== 'trunk' && node.branch !== 'race_affinity' && treeData.chosenBranch && treeData.chosenBranch !== node.branch) return false;
    for (const reqId of node.requires) {
      if (!treeData.allocated[reqId]) return false;
    }
    if (node.branchPointsRequired > 0) {
      const branchPts = Object.entries(treeData.allocated)
        .filter(([nId, pts]) => pts > 0 && treeData.nodes[nId]?.branch === node.branch)
        .reduce((sum, [nId]) => sum + (treeData.nodes[nId]?.cost || 0), 0);
      if (branchPts < node.branchPointsRequired) return false;
    }
    if (node.type === 'keystone') {
      const hasKeystone = Object.entries(treeData.allocated).some(([nId, pts]) =>
        pts > 0 && treeData.nodes[nId]?.type === 'keystone'
      );
      if (hasKeystone) return false;
    }
    return true;
  };

  const getNodeState = (nodeId) => {
    if (treeData.allocated[nodeId]) return 'allocated';
    if (canAllocate(nodeId)) return 'available';
    const node = treeData.nodes[nodeId];
    // race_affinity (Heritage) is exempt from branch-lock — never report it as branch-locked.
    if (node && node.branch !== 'trunk' && node.branch !== 'race_affinity' && treeData.chosenBranch && treeData.chosenBranch !== node.branch) {
      return 'branch-locked';
    }
    return 'locked';
  };

  const getBranchColor = (nodeId) => {
    const node = treeData.nodes[nodeId];
    if (!node) return '#555';
    const branch = treeData.branches.find(b => b.id === node.branch);
    return branch?.color || '#555';
  };

  const organizeNodes = () => {
    if (!treeData) return {};
    const organized = {};
    for (const [nodeId, node] of Object.entries(treeData.nodes)) {
      // Hide heritage nodes that don't apply to the character's race.
      // Players were seeing all 7 race-affinity nodes and getting confused
      // about why the 6 non-matching ones reported "prerequisites not met".
      if (Array.isArray(node.requiresRace) && node.requiresRace.length > 0) {
        if (!treeData.race || !node.requiresRace.includes(treeData.race)) continue;
      }
      const branch = node.branch;
      if (!organized[branch]) organized[branch] = [];
      organized[branch].push({ ...node, id: nodeId });
    }
    for (const branch of Object.keys(organized)) {
      organized[branch].sort((a, b) => {
        if (a.branchPointsRequired !== b.branchPointsRequired) return a.branchPointsRequired - b.branchPointsRequired;
        if (a.type === 'keystone' && b.type !== 'keystone') return -1;
        if (b.type === 'keystone' && a.type !== 'keystone') return 1;
        if ((a.levelRequired || 0) !== (b.levelRequired || 0)) return (a.levelRequired || 0) - (b.levelRequired || 0);
        return a.cost - b.cost;
      });
    }
    return organized;
  };

  // ============================================================
  // ADVANCED CLASS — fetch & actions
  // ============================================================
  const fetchACTree = useCallback(async () => {
    if (!character || !character.advanced_class) return;
    setAcLoading(true);
    setAcError(null);
    try {
      const data = await apiCall('/api/player/advanced-class-tree');
      setAcTree(data);
      try {
        const auras = typeof character.active_auras === 'string'
          ? JSON.parse(character.active_auras) : (character.active_auras || []);
        setActiveAuras(auras);
      } catch (e) { setActiveAuras([]); }
      try {
        const la = typeof character.learned_abilities === 'string'
          ? JSON.parse(character.learned_abilities) : (character.learned_abilities || []);
        setLearnedAbilities(la);
      } catch (e) { setLearnedAbilities([]); }
    } catch (e) {
      setAcError(e.message || 'Failed to load');
    } finally {
      setAcLoading(false);
    }
  }, [character, apiCall]);

  useEffect(() => {
    if (activeView === 'advanced' && character && character.advanced_class) fetchACTree();
  }, [activeView, character, fetchACTree]);

  const handleSpendACPoint = async (abilityId, choice) => {
    setAcBusy(abilityId);
    try {
      const body = { abilityId };
      if (choice) body.choice = choice;
      const data = await apiCall('/api/player/spend-ac-point', 'POST', body);
      setStatusMsg(`✅ ${data.message}`);
      if (onCharacterUpdate) onCharacterUpdate();
      fetchACTree();
    } catch (e) {
      setStatusMsg(`❌ ${e.message}`);
    } finally {
      setAcBusy(null);
      setTimeout(() => setStatusMsg(null), 3000);
    }
  };

  const handleToggleAura = async (auraName) => {
    setAcBusy(`aura_${auraName}`);
    try {
      const data = await apiCall('/api/player/toggle-aura', 'POST', { auraName });
      setStatusMsg(`✅ ${data.message}`);
      setActiveAuras(data.activeAuras || []);
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (e) {
      setStatusMsg(`❌ ${e.message}`);
    } finally {
      setAcBusy(null);
      setTimeout(() => setStatusMsg(null), 3000);
    }
  };

  const handleEquipLearnedAbility = async (abilityKey, equip) => {
    setAcBusy(`la_${abilityKey}`);
    try {
      const endpoint = equip ? 'equip-learned-ability' : 'unequip-learned-ability';
      const data = await apiCall(`/api/player/${endpoint}`, 'POST', { abilityKey });
      setStatusMsg(`✅ ${data.message}`);
      setLearnedAbilities(data.learnedAbilities || []);
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (e) {
      setStatusMsg(`❌ ${e.message}`);
    } finally {
      setAcBusy(null);
      setTimeout(() => setStatusMsg(null), 3000);
    }
  };

  // ============================================================
  // RENDER: Advanced Class Tab Content
  // ============================================================
  const renderACContent = () => {
    if (!character.advanced_class) {
      const level = character.level || 1;
      return (
        <div className="st-ac-content">
          <div className="st-ac-empty">
            {level < 40
              ? <><span className="st-ac-lock-icon">🔒</span> Advanced Classes unlock at <strong>level 40</strong>. You are level {level}.</>
              : <><span className="st-ac-lock-icon">⚜️</span> No Advanced Class selected yet. Return to the game — the selection modal should appear.</>
            }
          </div>
        </div>
      );
    }

    if (acLoading) return <div className="st-ac-content"><div className="st-ac-empty">Loading Advanced Class data...</div></div>;
    if (acError) return <div className="st-ac-content"><div className="st-ac-empty" style={{ color: '#f66' }}>Error: {acError}</div></div>;
    if (!acTree) return <div className="st-ac-content"><div className="st-ac-empty">Loading...</div></div>;

    const { acData, allocations, totalPoints, spentPoints, availablePoints } = acTree;
    const abilities = acData.abilities || {};

    const categoryOrder = ['passive', 'active', 'metamagic', 'system', 'ultimate'];
    const categoryLabels = {
      passive: '🔹 Passive Abilities', active: '⚡ Active Abilities', metamagic: '🔮 Metamagic',
      system: '⚙️ System', ultimate: '💥 Ultimate'
    };
    const categoryColors = {
      passive: '#4a6a8a', active: '#8a6a4a', metamagic: '#6a4a8a',
      system: '#5a5a5a', ultimate: '#8a4a4a'
    };

    const grouped = {};
    for (const [id, ab] of Object.entries(abilities)) {
      const cat = ab.category || 'passive';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push({ id, ...ab });
    }

    return (
      <div className="st-ac-content">
        {/* AC Header */}
        <div className="st-ac-header-section">
          <div className="st-ac-title-row">
            <span className="st-ac-icon">{acData.icon}</span>
            <h3 className="st-ac-name">{acData.name}</h3>
          </div>
          <p className="st-ac-desc">{acData.description}</p>
          <div className="st-ac-points-row">
            <div className="st-ac-point-box">
              <span className="st-ac-point-label">Total</span>
              <span className="st-ac-point-val">{totalPoints}</span>
            </div>
            <div className="st-ac-point-box">
              <span className="st-ac-point-label">Spent</span>
              <span className="st-ac-point-val">{spentPoints}</span>
            </div>
            <div className="st-ac-point-box available">
              <span className="st-ac-point-label">Available</span>
              <span className="st-ac-point-val" style={{ color: availablePoints > 0 ? '#4ade80' : '#888' }}>{availablePoints}</span>
            </div>
          </div>
        </div>

        {/* Abilities grouped by category */}
        {categoryOrder.map(cat => {
          const abils = grouped[cat];
          if (!abils || abils.length === 0) return null;
          const catColor = categoryColors[cat] || '#555';
          return (
            <div className="st-ac-category" key={cat}>
              <h4 className="st-ac-cat-title" style={{ borderBottomColor: catColor }}>
                {categoryLabels[cat] || cat}
              </h4>
              <div className="st-ac-ability-list">
                {abils.map(ab => {
                  const currentRank = (allocations || {})[ab.id] || 0;
                  const maxed = currentRank >= ab.maxRank;
                  const meetsPrereqs = (ab.requires || []).every(r => (allocations || {})[r] >= 1);
                  const canAlloc = !maxed && meetsPrereqs && availablePoints >= ab.cost;
                  const stateClass = maxed ? 'ac-maxed' : currentRank > 0 ? 'ac-partial' : meetsPrereqs ? 'ac-available' : 'ac-locked';

                  return (
                    <div key={ab.id} className={`st-ac-ability ${stateClass}`}>
                      <div className="st-ac-ability-top">
                        <span className="st-ac-ability-name">{ab.name}</span>
                        <span className={`st-ac-ability-rank ${maxed ? 'maxed' : ''}`}>
                          {currentRank}/{ab.maxRank} · {ab.cost} pt{ab.cost !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <span className="st-ac-ability-desc">{ab.description}</span>
                      {!meetsPrereqs && ab.requires && ab.requires.length > 0 && (
                        <span className="st-ac-ability-prereq">
                          🔒 Requires: {ab.requires.map(r => abilities[r]?.name || r).join(', ')}
                        </span>
                      )}
                      {canAlloc && ab.requiresChoice && (
                        <div className="st-ac-choice-row">
                          <select
                            className="st-ac-choice-select"
                            value={acChoices[ab.id] || ''}
                            onChange={(e) => setAcChoices(prev => ({ ...prev, [ab.id]: e.target.value }))}
                          >
                            <option value="">-- Choose --</option>
                            {(AC_CHOICE_OPTIONS[ab.requiresChoice] || []).map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                          <button
                            className="st-ac-alloc-btn"
                            disabled={acBusy === ab.id || !acChoices[ab.id]}
                            onClick={() => handleSpendACPoint(ab.id, acChoices[ab.id])}
                          >
                            {acBusy === ab.id ? '...' : 'Allocate'}
                          </button>
                        </div>
                      )}
                      {canAlloc && !ab.requiresChoice && (
                        <button
                          className="st-ac-alloc-btn"
                          disabled={acBusy === ab.id}
                          onClick={() => handleSpendACPoint(ab.id)}
                        >
                          {acBusy === ab.id ? '...' : `Allocate (${ab.cost} pt${ab.cost !== 1 ? 's' : ''})`}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Aura Management Section */}
        {(() => {
          const abilities2 = acData.abilities || {};
          const auraAbilities = Object.entries(abilities2).filter(([id, ab]) =>
            ab.effects && ab.effects.aura && (allocations[id] || 0) > 0
          );
          const auraSlotAbility = Object.entries(abilities2).find(([id, ab]) =>
            ab.effects && ab.effects.auraSlots && (allocations[id] || 0) > 0
          );
          const maxSlots = auraSlotAbility ? (auraSlotAbility[1].effects.auraSlots || 0) : 0;

          if (auraAbilities.length === 0 || maxSlots === 0) return null;

          return (
            <div className="st-ac-category">
              <h4 className="st-ac-cat-title" style={{ borderBottomColor: '#4ade80' }}>
                ✨ Aura Management ({activeAuras.length}/{maxSlots} slots)
              </h4>
              <div className="st-ac-ability-list">
                {auraAbilities.map(([id, ab]) => {
                  const auraName = ab.effects.aura;
                  const isActive = activeAuras.includes(auraName);
                  const rank = allocations[id] || 0;
                  return (
                    <div key={id} className={`st-ac-ability ${isActive ? 'ac-maxed' : 'ac-available'}`}>
                      <div className="st-ac-ability-top">
                        <span className="st-ac-ability-name">{ab.name} (Rank {rank})</span>
                        <span className={`st-ac-ability-rank ${isActive ? 'maxed' : ''}`}>
                          {isActive ? '🟢 Active' : '⚫ Inactive'}
                        </span>
                      </div>
                      <span className="st-ac-ability-desc">{ab.description}</span>
                      <button
                        className={`st-ac-alloc-btn ${isActive ? 'danger' : ''}`}
                        disabled={acBusy === `aura_${auraName}`}
                        onClick={() => handleToggleAura(auraName)}
                      >
                        {acBusy === `aura_${auraName}` ? '...' : (isActive ? 'Deactivate' : 'Activate')}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Mind Link: Learned Abilities Management (Telepath) */}
        {(() => {
          if (learnedAbilities.length === 0) return null;
          const abilities2 = acData.abilities || {};
          const mindLinkAbility = Object.entries(abilities2).find(([id, ab]) =>
            ab.effects && ab.effects.equippedAbilitySlots && (allocations[id] || 0) > 0
          );
          const maxLA = mindLinkAbility ? (mindLinkAbility[1].effects.equippedAbilitySlots || 0) : 0;
          const equippedCount = learnedAbilities.filter(a => a.equipped).length;

          return (
            <div className="st-ac-category">
              <h4 className="st-ac-cat-title" style={{ borderBottomColor: '#60a5fa' }}>
                🧠 Mind Link — Learned Abilities ({equippedCount}/{maxLA} equipped)
              </h4>
              <p className="st-ac-hint">
                Abilities learned from monsters via Mind Link. Equip them to use in combat (costs 10% MP).
              </p>
              <div className="st-ac-ability-list">
                {learnedAbilities.map(la => {
                  const isEquipped = !!la.equipped;
                  return (
                    <div key={la.key} className={`st-ac-ability ${isEquipped ? 'ac-maxed' : 'ac-available'}`}
                      style={{ borderLeftColor: isEquipped ? '#60a5fa' : undefined }}>
                      <div className="st-ac-ability-top">
                        <span className="st-ac-ability-name">{la.name}</span>
                        <span className="st-ac-ability-rank" style={{ color: isEquipped ? '#60a5fa' : '#888' }}>
                          {isEquipped ? '🔵 Equipped' : '⚫ Stored'}
                        </span>
                      </div>
                      <span className="st-ac-ability-desc">
                        {la.damage} {la.element ? la.element : la.type || ''} damage · from {la.source}
                      </span>
                      {maxLA > 0 && (
                        <button
                          className={`st-ac-alloc-btn ${isEquipped ? 'danger' : ''}`}
                          disabled={acBusy === `la_${la.key}` || (!isEquipped && equippedCount >= maxLA)}
                          onClick={() => handleEquipLearnedAbility(la.key, !isEquipped)}
                        >
                          {acBusy === `la_${la.key}` ? '...' : (isEquipped ? 'Unequip' : 'Equip')}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  // ============================================================
  // RENDER: Loading / Error states
  // ============================================================
  if (loading) {
    return (
      <div className="st-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="st-panel">
          <div className="st-loading">Loading skill tree...</div>
        </div>
      </div>
    );
  }

  if (error || !treeData) {
    return (
      <div className="st-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="st-panel">
          <div className="st-error">{error || 'Failed to load skill tree'}</div>
          <button className="st-close" onClick={onClose}>✖</button>
        </div>
      </div>
    );
  }

  const organized = organizeNodes();
  const branchesWithoutTrunk = treeData.branches.filter(b => b.id !== 'trunk');
  const hasAC = character.advanced_class || (character.level >= 40);

  return (
    <div className="st-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="st-panel">
        {/* HEADER */}
        <div className="st-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2>⚔️ Skills & Abilities</h2>
            <a
              href="/class-guide.html"
              target="_blank"
              rel="noopener noreferrer"
              title="Open Class &amp; Progression Guide"
              style={{
                fontSize: '0.75rem',
                color: '#80b0ff',
                textDecoration: 'none',
                background: 'rgba(100,140,255,0.1)',
                border: '1px solid rgba(100,140,255,0.25)',
                borderRadius: '4px',
                padding: '2px 8px',
                marginTop: '2px',
                whiteSpace: 'nowrap',
                transition: 'background 0.15s, border-color 0.15s',
              }}
              onMouseEnter={e => { e.target.style.background = 'rgba(100,140,255,0.2)'; e.target.style.borderColor = 'rgba(100,140,255,0.5)'; }}
              onMouseLeave={e => { e.target.style.background = 'rgba(100,140,255,0.1)'; e.target.style.borderColor = 'rgba(100,140,255,0.25)'; }}
            >📖 Guide</a>
          </div>
          <button className="st-close" onClick={onClose}>✖</button>
        </div>

        {statusMsg && <div className="st-status-bar">{statusMsg}</div>}

        {/* TOP-LEVEL TABS: Base Skills / Advanced Class */}
        <div className="st-view-tabs">
          <button
            className={`st-view-tab ${activeView === 'base' ? 'active' : ''}`}
            onClick={() => { setActiveView('base'); setSelectedNode(null); }}
          >
            🌳 {treeData.className} Skills
            {treeData.available > 0 && <span className="st-tab-badge">{treeData.available}</span>}
          </button>
          {hasAC && (
            <button
              className={`st-view-tab ${activeView === 'advanced' ? 'active' : ''}`}
              onClick={() => setActiveView('advanced')}
            >
              ⚜️ {character.advanced_class || 'Advanced Class'}
              {acTree && acTree.availablePoints > 0 && <span className="st-tab-badge">{acTree.availablePoints}</span>}
            </button>
          )}
        </div>

        {/* ============================================================
            BASE SKILL TREE VIEW
            ============================================================ */}
        {activeView === 'base' && (
          <>
            {/* Points display */}
            <div className="st-points-bar">
              <span className="st-points-badge">
                ⭐ {treeData.available} point{treeData.available !== 1 ? 's' : ''} available
              </span>
              <span className="st-points-total">
                ({treeData.totalAllocated}/{treeData.totalEarned} spent)
              </span>
              {treeData.chosenBranch && (
                <span className="st-spec-badge" style={{ color: treeData.branches.find(b => b.id === treeData.chosenBranch)?.color || '#ccc' }}>
                  {treeData.branches.find(b => b.id === treeData.chosenBranch)?.icon} {treeData.branches.find(b => b.id === treeData.chosenBranch)?.name} spec
                </span>
              )}
            </div>

            {/* BRANCH FILTER TABS */}
            <div className="st-branch-tabs">
              <button
                className={`st-branch-tab ${activeBranch === 'all' ? 'active' : ''}`}
                onClick={() => setActiveBranch('all')}
              >
                All
              </button>
              {treeData.branches.map(branch => (
                <button
                  key={branch.id}
                  className={`st-branch-tab ${activeBranch === branch.id ? 'active' : ''}`}
                  style={activeBranch === branch.id ? { borderColor: branch.color, color: branch.color } : {}}
                  onClick={() => setActiveBranch(branch.id)}
                >
                  {branch.icon} {branch.name}
                </button>
              ))}
            </div>

            {/* TREE CONTENT */}
            <div className="st-content" ref={contentRef}>
              {lines.length > 0 && (
                <svg className="st-lines-svg" style={{ width: svgSize.width, height: svgSize.height }}>
                  <defs>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="2" result="blur" />
                      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                  </defs>
                  {lines.map(line => (
                    <line
                      key={line.key}
                      x1={line.x1} y1={line.y1}
                      x2={line.x2} y2={line.y2}
                      stroke={line.color}
                      strokeWidth={line.isActive ? 2.5 : 1.5}
                      strokeDasharray={(!line.isActive && !line.isReady) ? '4 4' : 'none'}
                      opacity={line.isActive ? 0.9 : line.isReady ? 0.6 : 0.25}
                      filter={line.isActive ? 'url(#glow)' : 'none'}
                    />
                  ))}
                </svg>
              )}
              {organized.trunk && (activeBranch === 'all' || activeBranch === 'trunk') && (
                <div className="st-section">
                  <h3 className="st-section-title">⚔️ Shared Trunk</h3>
                  <div className="st-node-row">
                    {organized.trunk.map((node) => (
                      <SkillNode
                        key={node.id}
                        node={node}
                        state={getNodeState(node.id)}
                        branchColor={getBranchColor(node.id)}
                        isSelected={selectedNode === node.id}
                        isJustAllocated={recentlyAllocated === node.id}
                        nodeRef={el => { if (el) nodeRefs.current[node.id] = el; }}
                        onClick={() => setSelectedNode(selectedNode === node.id ? null : node.id)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {branchesWithoutTrunk.map(branch => {
                if (activeBranch !== 'all' && activeBranch !== branch.id) return null;
                const branchNodes = organized[branch.id];
                if (!branchNodes || branchNodes.length === 0) return null;

                // race_affinity (Heritage) is exempt from branch-lock — heritage stays unlocked even after specializing.
                const isBranchLocked = branch.id !== 'race_affinity' && treeData.chosenBranch && treeData.chosenBranch !== branch.id;

                const tiers = {};
                branchNodes.forEach(node => {
                  const tierKey = node.levelRequired ? `mastery_${node.levelRequired}` : `${node.branchPointsRequired}`;
                  if (!tiers[tierKey]) tiers[tierKey] = [];
                  tiers[tierKey].push(node);
                });

                const sortedTierEntries = Object.entries(tiers).sort(([a], [b]) => {
                  const aNum = a.startsWith('mastery_') ? 1000 + parseInt(a.split('_')[1]) : parseInt(a);
                  const bNum = b.startsWith('mastery_') ? 1000 + parseInt(b.split('_')[1]) : parseInt(b);
                  return aNum - bNum;
                });

                return (
                  <div key={branch.id} className={`st-section ${isBranchLocked ? 'st-branch-locked' : ''}`}>
                    <h3 className="st-section-title" style={{ borderColor: isBranchLocked ? '#444' : branch.color }}>
                      {branch.icon} {branch.name}
                      {isBranchLocked && <span className="st-locked-badge">🔒 Locked</span>}
                      {branch.desc && <span className="st-branch-desc">{branch.desc}</span>}
                    </h3>
                    {(() => {
                      let tierCounter = 0;
                      return sortedTierEntries.map(([tier, nodes]) => {
                        const hasMasteryNodes = nodes.some(n => n.levelRequired);
                        const masteryLevel = hasMasteryNodes ? nodes[0].levelRequired : null;
                        const tierNum = tier.startsWith('mastery_') ? null : Number(tier);
                        const isKeystone = nodes[0].type === 'keystone';
                        if (tierNum !== null && !isKeystone && !hasMasteryNodes) tierCounter++;
                        return (
                          <div key={tier} className="st-tier">
                            {(tierNum === null || tierNum >= 0) && (
                              <div className="st-tier-label">
                                {isKeystone ? '🔑 Keystone'
                                  : hasMasteryNodes ? `⚡ Mastery · Requires Level ${masteryLevel}`
                                  : `Tier ${tierCounter} · ${tierNum} branch pts`}
                              </div>
                            )}
                            <div className="st-node-row">
                              {nodes.map(node => (
                                <SkillNode
                                  key={node.id}
                                  node={node}
                                  state={getNodeState(node.id)}
                                  branchColor={isBranchLocked ? '#444' : branch.color}
                                  isSelected={selectedNode === node.id}
                                  isJustAllocated={recentlyAllocated === node.id}
                                  nodeRef={el => { if (el) nodeRefs.current[node.id] = el; }}
                                  onClick={() => setSelectedNode(selectedNode === node.id ? null : node.id)}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                );
              })}
            </div>

            {/* SELECTED NODE DETAIL */}
            {selectedNode && treeData.nodes[selectedNode] && (
              <NodeDetail
                node={treeData.nodes[selectedNode]}
                nodeId={selectedNode}
                state={getNodeState(selectedNode)}
                branchColor={getBranchColor(selectedNode)}
                treeData={treeData}
                onAllocate={handleAllocate}
                isAllocating={isAllocating}
              />
            )}

            {/* FOOTER ACTIONS */}
            <div className="st-footer">
              <div className="st-footer-info">
                <span className="st-gold-display">💰 {formatCurrency(treeData.gold || 0)} gold</span>
              </div>
              {/* v640.4: Soft-undo for last allocation within 60s */}
              {(() => {
                // eslint-disable-next-line no-unused-expressions
                undoTick; // re-render trigger so countdown updates
                const la = treeData.lastAllocation;
                if (!la || !la.expiresAt) return null;
                const remainingSec = Math.max(0, Math.ceil((la.expiresAt - Date.now()) / 1000));
                if (remainingSec <= 0) return null;
                const node = treeData.nodes[la.nodeId];
                return (
                  <button
                    className="st-undo-btn"
                    onClick={handleUndoSkill}
                    style={{
                      background: 'rgba(120, 100, 40, 0.35)',
                      border: '1px solid #c0a050',
                      color: '#ffd96b',
                      padding: '0.5rem 0.85rem',
                      borderRadius: 6,
                      cursor: 'pointer',
                      marginRight: '0.5rem',
                      fontSize: '0.92rem',
                    }}
                    title={`Refund ${node ? node.name : la.nodeId} (free, expires soon)`}
                  >
                    ↩️ Undo {node ? node.name : 'last'} ({remainingSec}s)
                  </button>
                );
              })()}
              <button
                className="st-respec-btn"
                disabled={treeData.totalAllocated === 0}
                onClick={() => setShowRespecConfirm(true)}
              >
                🔄 Respec ({formatCurrency(treeData.respecCost || 0)}g)
              </button>
            </div>

            {/* RESPEC CONFIRMATION */}
            {showRespecConfirm && (
              <div className="st-confirm-overlay" onClick={() => setShowRespecConfirm(false)}>
                <div className="st-confirm-box" onClick={e => e.stopPropagation()}>
                  <h3>🔄 Respec Skills?</h3>
                  <p>This will reset all allocated skill points.</p>
                  <p className="st-confirm-cost">
                    Cost: <span className="st-gold">{formatCurrency(treeData.respecCost || 0)} gold</span>
                  </p>
                  {treeData.gold < treeData.respecCost ? (
                    <p className="st-confirm-warn">❌ Not enough gold!</p>
                  ) : (
                    <p className="st-confirm-ok">You will get {treeData.totalEarned} points back.</p>
                  )}
                  <div className="st-confirm-btns">
                    <button className="st-btn-cancel" onClick={() => setShowRespecConfirm(false)}>Cancel</button>
                    <button
                      className="st-btn-confirm"
                      disabled={treeData.gold < treeData.respecCost}
                      onClick={handleRespec}
                    >
                      Confirm Respec
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ============================================================
            ADVANCED CLASS VIEW
            ============================================================ */}
        {activeView === 'advanced' && renderACContent()}
      </div>
    </div>
  );
};

// ============================================================
// Skill Node — individual node in the tree
// ============================================================
const SkillNode = ({ node, state, branchColor, isSelected, isJustAllocated, nodeRef, onClick }) => {
  const typeIcon = node.type === 'keystone' ? '🔑' : node.type === 'active' ? '⚡' : node.levelRequired ? '⭐' : '🔹';
  const deckBadge = node.type === 'active' ? '🃏' : null;

  return (
    <div
      ref={nodeRef}
      className={`st-node st-node-${state} ${isSelected ? 'selected' : ''} ${node.type === 'keystone' ? 'keystone' : ''} ${isJustAllocated ? 'st-just-allocated' : ''} ${node.levelRequired ? 'st-mastery' : ''}`}
      style={{
        '--branch-color': branchColor,
        borderColor: state === 'allocated' ? branchColor : undefined,
      }}
      onClick={onClick}
    >
      {isJustAllocated && (
        <div className="st-alloc-particles">
          <span className="st-particle" /><span className="st-particle" />
          <span className="st-particle" /><span className="st-particle" />
          <span className="st-particle" /><span className="st-particle" />
        </div>
      )}
      <div className="st-node-icon">{typeIcon}</div>
      <div className="st-node-name">{node.name}</div>
      {deckBadge && <div className="st-node-deck-badge" title="Adds a card to your combat deck">{deckBadge}</div>}
      <div className="st-node-cost">
        {node.cost > 1 ? `${node.cost} pts` : '1 pt'}
        {node.levelRequired && <span className="st-node-lvl-req"> · Lv{node.levelRequired}</span>}
      </div>
    </div>
  );
};

// ============================================================
// Node Detail — shows when a node is selected
// ============================================================
const NodeDetail = ({ node, nodeId, state, branchColor, treeData, onAllocate, isAllocating }) => {
  const prereqNames = node.requires.map(id => treeData.nodes[id]?.name || id);
  const deckImpact = node.deckImpact || [];

  // Compute the specific reason a 'locked' node can't be taken right now.
  // Distinguishes: missing skill points, level gate, branch-point gate,
  // unlearned prereqs, keystone exclusivity, race gate. "No skill points"
  // is reported as a separate (non-prereq) reason since it's a temporary
  // resource shortage, not a true prerequisite.
  const computeLockReason = () => {
    if (state !== 'locked') return null;
    // Race gate — should normally be filtered out of the UI entirely, but
    // surface a clear message if a heritage node ever slips through.
    if (Array.isArray(node.requiresRace) && node.requiresRace.length > 0) {
      if (!treeData.race || !node.requiresRace.includes(treeData.race)) {
        return `Requires race: ${node.requiresRace.join(' / ')} (you are ${treeData.race || 'unknown'})`;
      }
    }
    // Level gate
    if (node.levelRequired && treeData.level < node.levelRequired) {
      return `Requires level ${node.levelRequired} (you are level ${treeData.level})`;
    }
    // Unlearned prereqs
    const missingPrereqs = (node.requires || [])
      .filter(reqId => !treeData.allocated[reqId])
      .map(reqId => treeData.nodes[reqId]?.name || reqId);
    if (missingPrereqs.length > 0) {
      return `Learn first: ${missingPrereqs.join(', ')}`;
    }
    // Branch points gate
    if (node.branchPointsRequired > 0) {
      const branchPts = Object.entries(treeData.allocated)
        .filter(([nId, pts]) => pts > 0 && treeData.nodes[nId]?.branch === node.branch)
        .reduce((sum, [nId]) => sum + (treeData.nodes[nId]?.cost || 0), 0);
      if (branchPts < node.branchPointsRequired) {
        const branchInfo = treeData.branches.find(b => b.id === node.branch);
        const branchName = branchInfo ? branchInfo.name : node.branch;
        return `Spend ${node.branchPointsRequired - branchPts} more point${node.branchPointsRequired - branchPts === 1 ? '' : 's'} in ${branchName}`;
      }
    }
    // Keystone exclusivity
    if (node.type === 'keystone') {
      const hasKeystone = Object.entries(treeData.allocated).some(([nId, pts]) =>
        pts > 0 && treeData.nodes[nId]?.type === 'keystone'
      );
      if (hasKeystone) return 'You already have a keystone. Respec to change.';
    }
    // SP shortage — the only thing left blocking allocation
    if (treeData.available < node.cost) {
      return treeData.available <= 0
        ? 'No skill points available — level up to earn more'
        : `Not enough skill points (need ${node.cost}, have ${treeData.available})`;
    }
    return null;
  };

  const lockReason = computeLockReason();

  return (
    <div className="st-detail" style={{ borderColor: branchColor }}>
      <div className="st-detail-header">
        <span className="st-detail-type" style={{ color: branchColor }}>
          {node.type === 'keystone' ? '🔑 KEYSTONE' : node.type === 'active' ? '⚡ ACTIVE' : node.levelRequired ? '⚡ MASTERY' : '🔹 PASSIVE'}
        </span>
        <h3 className="st-detail-name">{node.name}</h3>
      </div>
      <p className="st-detail-desc">{node.description}</p>

      {/* Deck Impact Section */}
      {deckImpact.length > 0 && (
        <div className="st-detail-deck-impact">
          <span className="st-detail-label">Deck Impact:</span>
          <div className="st-deck-impact-list">
            {deckImpact.map((impact, i) => (
              <div key={i} className="st-deck-impact-item">{impact}</div>
            ))}
          </div>
        </div>
      )}

      {prereqNames.length > 0 && (
        <div className="st-detail-prereqs">
          <span className="st-detail-label">Requires:</span> {prereqNames.join(', ')}
        </div>
      )}
      {node.branchPointsRequired > 0 && (
        <div className="st-detail-prereqs">
          <span className="st-detail-label">Branch points needed:</span> {node.branchPointsRequired}
        </div>
      )}
      {node.levelRequired && (
        <div className="st-detail-prereqs" style={{ color: treeData.level >= node.levelRequired ? '#4a6' : '#e55' }}>
          <span className="st-detail-label">Level required:</span> {node.levelRequired}
          {treeData.level < node.levelRequired && ` (you are level ${treeData.level})`}
        </div>
      )}
      <div className="st-detail-cost">
        <span className="st-detail-label">Cost:</span> {node.cost} skill point{node.cost > 1 ? 's' : ''}
      </div>
      {state === 'available' && (
        <button
          className="st-allocate-btn"
          style={{ background: branchColor }}
          disabled={isAllocating}
          onClick={() => onAllocate(nodeId)}
        >
          {isAllocating ? 'Learning...' : `Learn ${node.name}`}
        </button>
      )}
      {state === 'allocated' && (
        <div className="st-detail-learned">✅ Learned</div>
      )}
      {state === 'branch-locked' && (
        <div className="st-detail-locked">🔒 Branch Locked — You chose a different specialization. Respec to change.</div>
      )}
      {state === 'locked' && (
        <div className="st-detail-locked">🔒 {lockReason || 'Prerequisites not met'}</div>
      )}
    </div>
  );
};

export default SkillTreePanel;
